import { Box, IconButton, ToggleButton, Tooltip } from "@wix/design-system";
import { useTranslation, useExperiments } from "@wix/fe-essentials-standalone";
import {
  ArrowRight,
  ArrowLeft,
  Confirm,
  Microphone,
} from "@wix/wix-ui-icons-common";
import type { FC } from "react";

interface ActionButtonsProps {
  sizeMapByElementType: any;
  toggleMicrophone: () => void;
  isListening: boolean;
  handleGenerateSiteClick: () => void;
  prompt: string;
  generatingProject: boolean;
  isMicPermissionDenied: boolean;
  isBudgetExceeded?: boolean;
}

export const ActionButtons: FC<ActionButtonsProps> = ({
  sizeMapByElementType,
  toggleMicrophone,
  isListening,
  handleGenerateSiteClick,
  prompt,
  generatingProject,
  isMicPermissionDenied,
  isBudgetExceeded,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const isForceHebrewEnabled = experiments.enabled("specs.picasso.forceHebrew");
  const ArrowIcon = isForceHebrewEnabled ? ArrowLeft : ArrowRight;

  // Show confirm button when mic permission is denied and there's a prompt
  if (isMicPermissionDenied) {
    return (
      <IconButton
        size={sizeMapByElementType.textAreaButton}
        onClick={handleGenerateSiteClick}
        disabled={generatingProject}
        data-testid="confirm-button"
        labelValue={t("landing.generate_site")}
      >
        <ArrowIcon />
      </IconButton>
    );
  }

  if (!prompt || isListening) {
    return (
      <Tooltip
        content={
          <>
            {t("landing.budgetExceeded.tooltip.title")}
            <br />
            {t("landing.budgetExceeded.tooltip.description")}
          </>
        }
        disabled={!isBudgetExceeded}
        maxWidth="310px"
        size="medium"
        moveBy={{ y: 10 }}
      >
        <IconButton
          size={sizeMapByElementType.textAreaButton}
          onClick={toggleMicrophone}
          data-testid="microphone-button"
          selected={isListening}
          labelValue={t("landing.start_listening")}
          disabled={isBudgetExceeded}
        >
          {isListening ? <Confirm /> : <Microphone />}
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Box gap={1} direction="horizontal">
      {!isMicPermissionDenied && (
        <ToggleButton
          size={sizeMapByElementType.textAreaButton}
          onClick={toggleMicrophone}
          data-testid="microphone-button"
          selected={isListening}
          labelValue={t("landing.start_listening")}
          disabled={isBudgetExceeded}
        >
          <Microphone />
        </ToggleButton>
      )}
      <IconButton
        size={sizeMapByElementType.textAreaButton}
        onClick={handleGenerateSiteClick}
        disabled={generatingProject || isBudgetExceeded}
      >
        <ArrowIcon />
      </IconButton>
    </Box>
  );
};
